import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import {
    Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle,
    FormControlLabel, Icon, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@material-ui/core';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';

import { resolveMarketplaceAmi, fetchAwsSoftwarePricing, clearAwsLaunchChecks } from '../store/actions/cluster.actions';
import MarketplaceSubscribePrompt from './MarketplaceSubscribePrompt';
import * as formHelpers from './formHelpers';
import {
    isEfaCompatible, areZonesDifferent, effectiveZone, headZoneInstanceIds, unofferedShapeReason, imageReason, licenceFor,
    awsImageOptionState, awsStackOptionState,
} from './formHelpers';
import useAwsCatalog from './useAwsCatalog';
import useLaunchCheck from './useLaunchCheck';
import usePinScopeReset from './usePinScopeReset';
import AwsImageChips from './AwsImageChips';
import { imageShapeCompatibility, describeImage, softwareEntryFor, softwareRateFor, totalHourly } from './imageCompat';
import DeleteImageDialog from './DeleteImageDialog';
import useProviderData from '../hooks/useProviderData';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import BasicInformationSection from '../components/BasicInformationSection';
import ImageSelectionSection from '../components/ImageSelectionSection';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration';
import StorageSettingsSection from '../components/StorageSettingsSection';
import NetworkSettingsSection from '../components/NetworkSettingsSection';
import CapacityReservationsSection from '../components/CapacityReservationsSection';
import DescriptionSection from '../components/DescriptionSection';
import CredentialSelector from '../components/CredentialSelector';
import RegionSelector from '../components/RegionSelector';
import ZoneSelector from '../components/ZoneSelector';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import VolumeManager from '../components/VolumeManager';
import HeadNodePDSSection from '../ibm/HeadNodePDSSection';
import StorageConfigurationSection from '../components/StorageConfigurationSection';
import { ZoneAvailabilityIndicator, PricingPermissionHint } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';

// A zone is required on AWS: with none, terraform picks the first zone for the
// instance while the subnet gets whatever AWS chooses, and the offering checks
// below have nothing to check against.
const requireZone = (f) => (f.zoneId ? true : 'zone');

const stackTypeChipStyle = { background: '#e0f2fe', color: '#075985' };

const AwsCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace,
        disableInputs, isFormValid: baseFormValid, formInvalidReason: baseInvalidReason, volSizeLimits,
        quotas, handleClose, handleSubmit, handleEdit, dispatch,
    } = useClusterForm({
        provider: 'aws', props, formHelpers,
        extraValidation: requireZone,
        // The shared providers.json-based zone check is replaced by the live catalog below.
        availabilityOptions: { enabled: false },
    });

    // Credential-first, live from AWS: regions for the credential, then zones,
    // shapes, per-zone offerings and prices for the region. The selectors use
    // it to make a zone x shape combination AWS would reject unselectable.
    const catalog = useAwsCatalog({ credentialId: form?.credentialId, regionId: form?.regionId });
    const instanceSizes = catalog.shapes;
    const marketplaceAmiState = useSelector(s => s.clusters?.cluster?.marketplaceAmi);

    // --- Images -----------------------------------------------------------
    // AWS is unified-only: one image boots the head node and every compute
    // node. The picker lists Heidi stacks and the Heidi images that actually
    // exist in this credential's account and region; the head image is the
    // exact AMI pinned by a version or cloud image pick, else the newest
    // "<prefix>-heidi-*" image, the way terraform resolves it.
    const stacks = useSelector(({ stacks }) => stacks.stack.list) || [];
    const { credentials: awsCredentials } = useProviderData('aws');
    const credentialsById = useMemo(() => Object.fromEntries((awsCredentials || []).map((c) => [c.id, c.name])), [awsCredentials]);
    const credentialName = credentialsById[form?.credentialId] || null;
    const headImage = useMemo(() => {
        if (!catalog.imagesReady || !form) return null;
        if (form.serverImageId) return catalog.imageById(form.serverImageId);
        return catalog.resolveImage(form.serverImageName, 'heidi');
    }, [catalog, form?.serverImageId, form?.serverImageName]); // eslint-disable-line react-hooks/exhaustive-deps
    // Marketplace variants resolve their own AMI; use it for compatibility when its attributes are known.
    const compatImage = marketplace.isMarketStack
        ? (marketplaceAmiState?.imageId && marketplaceAmiState.arch ? marketplaceAmiState : null)
        : headImage;
    // --- Marketplace software fees --------------------------------------
    // An image built from a Marketplace product (E4S, ParaTools) carries the
    // product code, and running it costs the instance price PLUS the
    // product's software fee. Product names come from Heidi's marketplace
    // entries; amounts come from the account's own agreements.
    const marketplaceApps = useSelector(({ marketStack }) => marketStack?.marketStack?.list) || [];
    const productNameByCode = useMemo(() => {
        const map = {};
        (marketplaceApps || []).forEach((app) => {
            const codes = [app.config?.marketplace_product_code, ...((app.variants || []).map((v) => v.config?.marketplace_product_code))].filter(Boolean);
            codes.forEach((code) => { if (!map[code]) map[code] = app.name || app.title || app.id; });
        });
        return map;
    }, [marketplaceApps]);
    const decorateSoftware = useCallback((entry) => (
        entry ? { ...entry, productName: productNameByCode[entry.productCode] || (entry.productId ? `Marketplace product ${entry.productId}` : 'this Marketplace product') } : null
    ), [productNameByCode]);
    // Marketplace-AMI path: price the resolved AMI's product code on demand.
    const marketplaceCodes = useMemo(() => (marketplace.isMarketStack ? (marketplaceAmiState?.productCodes || []) : []), [marketplace.isMarketStack, marketplaceAmiState?.productCodes]);
    const softwarePricingByKey = useSelector(({ clusters }) => clusters.cluster.awsCatalog.softwarePricing) || {};
    const marketplacePricingKey = marketplaceCodes.length && form?.credentialId && form?.regionId
        ? `${form.credentialId}|${form.regionId}|${[...marketplaceCodes].sort().join(',')}` : null;
    useEffect(() => {
        if (!marketplacePricingKey || softwarePricingByKey[marketplacePricingKey]) return;
        dispatch(fetchAwsSoftwarePricing({ credentialId: form.credentialId, region: form.regionId, productCodes: marketplaceCodes }));
    }, [dispatch, marketplacePricingKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const softwareEntry = useMemo(() => {
        if (marketplace.isMarketStack) {
            const pricing = marketplacePricingKey ? softwarePricingByKey[marketplacePricingKey] : null;
            const image = marketplaceAmiState?.imageId ? { productCodes: marketplaceCodes } : null;
            return decorateSoftware(softwareEntryFor(image, pricing && !pricing.loading ? pricing : null));
        }
        return decorateSoftware(catalog.softwareFor(headImage));
    }, [marketplace.isMarketStack, marketplacePricingKey, softwarePricingByKey, marketplaceAmiState?.imageId, marketplaceCodes, decorateSoftware, catalog, headImage]);
    const licence = useMemo(() => licenceFor(softwareEntry, softwareEntry?.productName), [softwareEntry]);
    const priceFor = useCallback((inst) => {
        if (!softwareEntry) return inst.price;
        const software = softwareRateFor(softwareEntry, inst.instance_id);
        return { instance: inst.price, software, total: totalHourly(inst.price, software) };
    }, [softwareEntry]);
    const softwareText = (entry) => {
        if (!entry) return '';
        if (entry.subscribed === false) return ' · Marketplace subscription required';
        const amount = entry.flatRate != null ? `$${entry.flatRate.toFixed(2)}/hr` : entry.minRate != null ? `from $${entry.minRate.toFixed(2)}/hr` : 'fee applies, amount unknown';
        const trial = entry.freeTrial && entry.freeTrial.days ? ` (${entry.freeTrial.days}-day free trial)` : '';
        return ` · software ${amount}${trial}`;
    };

    // Picker rows. Stacks: every AWS stack record, disabled with the reason when
    // it has no usable image here. Cloud images: every Heidi image in the account
    // and region, stack-owned ones included, the same as IBM.
    const awsStacks = useMemo(() => stacks.filter((s) => s && s.provider === 'aws' && s.data && s.data.prefix), [stacks]);
    const stackByPrefix = useMemo(() => {
        const map = {};
        awsStacks.forEach((s) => { if (!map[s.data.prefix]) map[s.data.prefix] = s; });
        return map;
    }, [awsStacks]);
    const softwareOf = useCallback((image) => decorateSoftware(catalog.softwareFor(image)), [catalog, decorateSoftware]);
    const licenceOfImage = useCallback((image) => {
        const software = softwareOf(image);
        return licenceFor(software, software?.productName);
    }, [softwareOf]);
    const cloudImages = useMemo(() => {
        if (!form?.credentialId || !form?.regionId) return [];
        return (catalog.images || [])
            .filter((image) => image.kind === 'heidi')
            .slice()
            .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
            .map((image) => ({
                id: image.imageId,
                name: image.name,
                arch: image.arch,
                os: '',
                // AWS images carry no OS; the stack built under the same prefix knows it.
                osType: (stackByPrefix[image.prefix] && stackByPrefix[image.prefix].data.type) || '',
                createdAt: image.createdAt,
                image,
                software: softwareOf(image),
            }));
    }, [form?.credentialId, form?.regionId, catalog.images, stackByPrefix, softwareOf]);

    // A pinned AMI id is scoped to one account and region: drop it when either changes.
    usePinScopeReset(form, setField);

    // AWS's own verdict (run-instances --dry-run) on each complete image + shape + zone.
    const launchRequests = useMemo(() => {
        const zoneName = (zoneId) => (zoneId ? catalog.zoneNameById[zoneId] || null : null);
        const reqs = [{ role: 'head node', imageId: compatImage?.imageId, instanceType: form?.serverInstanceId, zoneName: zoneName(form?.zoneId) }];
        (form?.nodeGroups || []).forEach((g, i) => {
            if ((g.count == null ? 1 : Number(g.count)) <= 0 || !g.instance_id) return;
            reqs.push({ role: `compute group ${i + 1}`, imageId: compatImage?.imageId, instanceType: g.instance_id, zoneName: zoneName(effectiveZone(g, form)) });
        });
        return reqs;
    }, [compatImage, form, catalog.zoneNameById]);
    const launchCheck = useLaunchCheck({
        credentialId: form?.credentialId, region: form?.regionId, requests: launchRequests, enabled: catalog.catalogReady,
    });

    // Subscription prompt: the account has no agreement for the product (or AWS
    // said OptInRequired at dry run). Re-check refetches pricing and forgets
    // cached dry-run verdicts so a fresh subscription is picked up.
    const launchOptIn = (launchCheck.rejected || []).find((r) => r.code === 'OptInRequired');
    const needsSubscription = Boolean(softwareEntry && softwareEntry.subscribed === false) || Boolean(launchOptIn);
    const pricingChecking = Boolean(marketplacePricingKey && softwarePricingByKey[marketplacePricingKey]?.loading) || catalog.catalogLoading;
    const recheckSubscription = useCallback(() => {
        dispatch(clearAwsLaunchChecks());
        if (marketplace.isMarketStack && marketplaceCodes.length) {
            dispatch(fetchAwsSoftwarePricing({ credentialId: form.credentialId, region: form.regionId, productCodes: marketplaceCodes }));
        } else {
            catalog.retry();
        }
    }, [dispatch, marketplace.isMarketStack, marketplaceCodes, form?.credentialId, form?.regionId, catalog]);
    const subscribeListingUrl = softwareEntry?.listingUrl || marketplace.config?.moreInfo_link || marketplace.config?.head_image_link || null;

    // Delete-image flow opened from the image picker (nested dialog: the global
    // dialog already holds this form).
    const [deleteRow, setDeleteRow] = useState(null);

    // Safety net for combinations that became invalid after the fact (edited
    // cluster, an image deleted since, data arriving after a click): block Save with the reason.
    const awsInvalidReason = useMemo(() => (
        unofferedShapeReason(form, catalog)
        || imageReason(form, { catalog, headImage: compatImage, launchCheck, marketplace, marketplaceAmi: marketplaceAmiState, licence, software: softwareEntry })
    ), [form, catalog, compatImage, launchCheck, marketplace, marketplaceAmiState, licence, softwareEntry]);
    const isFormValid = baseFormValid && !awsInvalidReason;
    // One sentence listing everything that is missing, base fields and AWS
    // reasons together, so an image or zone problem is visible from the start
    // rather than only after the last required field is filled.
    const formInvalidReason = useMemo(() => {
        if (baseFormValid && !awsInvalidReason) return '';
        const prefix = 'Complete the following before saving: ';
        const baseList = !baseFormValid && baseInvalidReason.startsWith(prefix) ? baseInvalidReason.slice(prefix.length).replace(/\.$/, '') : '';
        if (!baseFormValid && !baseInvalidReason.startsWith(prefix)) return baseInvalidReason;
        return `${prefix}${[baseList, awsInvalidReason].filter(Boolean).join(', ')}.`;
    }, [baseFormValid, baseInvalidReason, awsInvalidReason]);

    // Storage configuration state
    const [availableStorageConfigs, setAvailableStorageConfigs] = useState([]);
    const volumeDropdownAnchorEl = useRef({});
    const [volumeDropdownOpen, setVolumeDropdownOpen] = useState({});

    const zonesDiffer = useMemo(() => areZonesDifferent(form), [form]);

    // The zone dropdown stores a zone_id (usw2-az1). Terraform needs the zone
    // NAME (us-west-2a) for anything that cannot derive an AZ from the subnet -
    // fast snapshot restore builds its resource id out of it, and capacity
    // reservations compare against it. The name comes from the live catalog:
    // the letter<->id mapping is per AWS account, so a static guess is wrong.
    useEffect(() => {
        if (!catalog.catalogReady) return;
        const derived = (form?.zoneId && catalog.zoneNameById[form.zoneId]) || '';
        if (derived !== (form?.zoneName || '')) setField('zoneName', derived);
    }, [catalog.catalogReady, catalog.zoneNameById, form?.zoneId, form?.zoneName, setField]);

    // Head-zone verdict for the card chip: per-zone when a zone is chosen,
    // region roll-up ("offered in 4 of 6 zones") when not.
    const indicator = useMemo(() => {
        const base = { isLoading: catalog.catalogLoading, error: catalog.catalogError, unableToVerify: false };
        if (!catalog.catalogReady) return { ...base, isAvailable: null };
        const ids = headZoneInstanceIds(form);
        const provisions = (g) => (g.count == null ? 1 : Number(g.count)) > 0;
        const ownZoneGroups = (form?.nodeGroups || []).filter((g) => g.zone_id && g.instance_id && provisions(g));
        if (form?.zoneId) {
            const missing = ids.filter((id) => catalog.isOffered(form.zoneId, id) === false);
            ownZoneGroups.forEach((g) => { if (catalog.isOffered(g.zone_id, g.instance_id) === false) missing.push(g.instance_id); });
            return { ...base, isAvailable: missing.length === 0, missingInstances: [...new Set(missing)] };
        }
        if (ids.length === 0) return { ...base, isAvailable: null };
        const offeredZoneCount = catalog.zones.filter((z) => ids.every((id) => catalog.isOffered(z.zone_id, id) !== false)).length;
        const nowhere = ids.filter((id) => catalog.isOfferedInRegion(id) === false);
        return {
            ...base, isAvailable: null,
            regionSummary: { offeredZoneCount, totalZoneCount: catalog.zones.length, missingInstances: [...new Set(nowhere)] },
        };
    }, [catalog, form]);

    // Field-level errors for the head node row (the safety-net case).
    const headZoneMissing = useMemo(() => (
        form?.zoneId && catalog.catalogReady
            ? headZoneInstanceIds(form).filter((id) => catalog.isOffered(form.zoneId, id) === false)
            : []
    ), [form, catalog]);
    const headShapeUnoffered = Boolean(form?.serverInstanceId && catalog.catalogReady && (
        form?.zoneId ? catalog.isOffered(form.zoneId, form.serverInstanceId) === false
            : catalog.isOfferedInRegion(form.serverInstanceId) === false
    ));
    const headCompat = compatImage && form?.serverInstanceId ? imageShapeCompatibility(compatImage, catalog.shapeById(form.serverInstanceId), licence) : { compatible: true };
    const headLaunch = launchCheck.byRole['head node'];
    const headShapeHelper = headShapeUnoffered
        ? (form.zoneId ? `Not offered in ${catalog.zoneLabel(form.zoneId)}` : `Not offered in ${form.regionId}`)
        : !headCompat.compatible ? headCompat.shapeReason
            : headLaunch && headLaunch.ok === false ? `AWS: ${headLaunch.message}` : undefined;

    // Image field decorations.
    const imageMissing = Boolean(catalog.imagesReady && !marketplace.isMarketStack && form?.serverImageName && !headImage);
    const imageHelperText = imageMissing
        ? (form.serverImageId
            ? `The selected image no longer exists in ${form.regionId}. Pick another image.`
            : `No ${form.serverImageName} image in ${form.regionId} for this credential. Pick another image or build the stack there.`)
        : undefined;
    const imageInfoText = headImage
        ? describeImage(headImage) + softwareText(softwareEntry)
        : marketplace.isMarketStack && marketplaceAmiState?.imageId
            ? `${marketplaceAmiState.name} · ${marketplaceAmiState.arch || ''}${softwareText(softwareEntry)}`
            : (catalog.imageCatalog && catalog.imageCatalog.status !== 'ok' ? `Could not list images from AWS: ${catalog.imageCatalog.message}` : undefined);
    const legacyImageNote = form?.wasMultiImage ? 'This cluster used Multi-Image (Legacy) images and will be saved as Unified.' : undefined;
    const cloudImagesPlaceholder = !form?.credentialId || !form?.regionId
        ? 'Select a credential and region first'
        : (catalog.imageCatalog && catalog.imageCatalog.status !== 'ok'
            ? `Could not list images from AWS: ${catalog.imageCatalog.message}`
            : `No Heidi images for this credential in ${form.regionId}`);

    // Whole-cluster estimate: head + every provisioning group, instance + software.
    const clusterEstimate = useMemo(() => {
        if (!catalog.catalogReady || !form?.serverInstanceId) return null;
        const hourly = (instanceId) => {
            const price = catalog.priceOf(instanceId);
            if (price == null) return null;
            if (!softwareEntry) return price;
            const sw = softwareRateFor(softwareEntry, instanceId);
            return sw == null && softwareEntry.subscribed !== false && !softwareEntry.unknown ? null : totalHourly(price, sw);
        };
        let total = hourly(form.serverInstanceId);
        if (total == null) return null;
        for (const g of form.nodeGroups || []) {
            const count = g.count == null ? 1 : Number(g.count);
            if (count <= 0 || !g.instance_id) continue;
            const h = hourly(g.instance_id);
            if (h == null) return null;
            total += count * h;
        }
        return { total, trial: Boolean(softwareEntry && softwareEntry.freeTrial && softwareEntry.freeTrial.days) };
    }, [catalog, form?.serverInstanceId, form?.nodeGroups, softwareEntry]);

    // Marketplace AMI resolution.
    //
    // Two paths coexist:
    //  (1) Dynamic - the active variant config declares `marketplace_product_code`.
    //      Hit POST /api/marketplace/resolve-ami, which calls describe-images in
    //      the chosen region filtered by product-code and returns the latest AMI.
    //      Result flows into Redux at clusters.cluster.marketplaceAmi; the second
    //      effect below mirrors it into the form's AMI fields.
    //  (2) Static - legacy entries (Azure/GCP/IBM/ClassifyX) still carry a
    //      `marketplace_images.<sku>.<region>` dict. Same code path as before.
    //
    // marketplace.config is the active (product + variant-merged) config when the
    // entry declares variants[], so either field set is visible at the same level.
    useEffect(() => {
        if (!marketplace.isMarketStack || !form?.regionId) return;

        const cfg = marketplace.config || {};
        const productCode = cfg.marketplace_product_code;

        if (productCode) {
            // Dynamic path - dispatch and let the mirror effect below sync the
            // resolved AMI into form.marketplace*ImageId.
            if (!form?.credentialId) {
                // No credential picked yet - clear AMI so submit stays blocked.
                setField({ marketplaceHeadImageId: null, marketplaceComputeImageId: null });
                return;
            }
            dispatch(resolveMarketplaceAmi({
                credentialId: form.credentialId,
                region: form.regionId,
                productCode: productCode,
                architecture: marketplace.selectedArch,
                nameMustContain: cfg.name_must_contain || undefined,
            }));
            return;
        }

        // Static legacy path
        if (!cfg.marketplace_images) return;
        const regionImages = cfg.marketplace_images?.e4s?.[form.regionId];
        if (regionImages && typeof regionImages === 'object' && regionImages.head_image_id) {
            setField({
                marketplaceHeadImageId: regionImages.head_image_id || null,
                marketplaceComputeImageId: regionImages.compute_image_id || null,
            });
        } else {
            setField({ marketplaceHeadImageId: null, marketplaceComputeImageId: null });
        }
    }, [marketplace.isMarketStack, marketplace.config, marketplace.selectedArch,
        form?.regionId, form?.credentialId, dispatch, setField]);

    // Mirror the dynamically-resolved AMI from Redux into the form's scalar
    // fields. Only applies when the active variant uses the dynamic path -
    // legacy entries set form fields directly in the effect above.
    useEffect(() => {
        if (!marketplace.isMarketStack) return;
        if (!marketplace.config?.marketplace_product_code) return;
        if (marketplaceAmiState?.imageId) {
            // For Marketplace AMIs, head and compute are the same image.
            setField({
                marketplaceHeadImageId: marketplaceAmiState.imageId,
                marketplaceComputeImageId: marketplaceAmiState.imageId,
            });
        } else {
            setField({ marketplaceHeadImageId: null, marketplaceComputeImageId: null });
        }
    }, [marketplace.isMarketStack, marketplace.config?.marketplace_product_code,
        marketplaceAmiState?.imageId, setField]);

    // Storage-managed node sync
    const handleStorageConfigurationChange = useCallback((storageConfigurations, metadata = {}) => {
        if (!form) return;
        setAvailableStorageConfigs(storageConfigurations || []);

        const currentGroups = form.nodeGroups || [];
        const regularGroups = currentGroups.filter(g => !g.storageReference && g.nodeType !== 'media');
        const managedGroups = [];
        const processedIds = new Set();

        const genRef = (config, nodeType) => {
            if (config.id) return `${nodeType}-${config.id}`;
            const hash = JSON.stringify({ name: config.name, nodeType, mediaType: config.mediaType, stackName: config.stackName })
                .split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
            return `${nodeType}-${config.name || 'unnamed'}-${Math.abs(hash)}`;
        };

        (storageConfigurations || []).forEach(config => {
            if (!config.mediaType || !config.stackName?.trim() ||
                (config.provider !== 'volumez' && config.providerType !== 'volumez')) return;

            const ref = genRef(config, 'media');
            processedIds.add(ref);
            const existing = currentGroups.find(g => g.storageReference === ref)
                || currentGroups.find(g => g.nodeType === 'media' && g.instance_id === config.mediaType
                    && (g.group_node_prefix === config.stackName || (!g.group_node_prefix && !config.stackName)));

            managedGroups.push({
                ...(existing || {}),
                _id: existing?._id || false,
                id: existing?.id || ref,
                instance_id: config.mediaType,
                group_node_prefix: config.stackName || existing?.group_node_prefix || '',
                group_node_prefix_label: config.name || existing?.group_node_prefix_label || `Media Storage ${config.id}`,
                count: (config.requiredNodes > 0) ? config.requiredNodes : (existing?.count || 1),
                image_type: existing?.image_type || null,
                efa: existing?.efa || false,
                zone_id: config.mediaZoneId || existing?.zone_id || null,
                storageReference: ref,
                nodeType: 'media',
            });
        });

        const validConfigIds = new Set((storageConfigurations || []).map(c => c.id));
        const allGroups = [...regularGroups, ...managedGroups].map(g => {
            if (g.attachedVolumes?.length > 0) {
                const valid = g.attachedVolumes.filter(v => validConfigIds.has(v.storageConfigId));
                if (valid.length !== g.attachedVolumes.length) return { ...g, attachedVolumes: valid };
            }
            return g;
        });
        const headVolumes = (form.serverAttachedVolumes || []).filter(v => validConfigIds.has(v.storageConfigId));
        setField({
            storageConfigurations: metadata.persistedConfigs || [],
            nodeGroups: allGroups,
            serverAttachedVolumes: headVolumes,
        });
    }, [form, setField]);

    // Compute node volume operations
    const addVolumeToNode = useCallback((nodeIndex, storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        const headVolume = config.headNodeVolumes?.[0];
        const newVolume = {
            id: `node-${nodeIndex}-volume-${Date.now()}`,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            overrides: { capacity: headVolume?.overrides?.capacity || null, iops: headVolume?.overrides?.iops || null, throughput: headVolume?.overrides?.throughput || null },
            status: 'pending',
        };
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...updated[nodeIndex], attachedVolumes: [...(updated[nodeIndex].attachedVolumes || []), newVolume] };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, availableStorageConfigs, setField]);

    const removeVolumeFromNode = useCallback((nodeIndex, volumeId) => {
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...updated[nodeIndex], attachedVolumes: (updated[nodeIndex].attachedVolumes || []).filter(v => v.id !== volumeId) };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    const handleVolumeUpdate = useCallback((nodeIndex, updatedVolume) => {
        const group = (form.nodeGroups || [])[nodeIndex];
        if (!group?.attachedVolumes) return;
        const idx = group.attachedVolumes.findIndex(v => v.id === updatedVolume.id);
        if (idx === -1) return;
        const vols = [...group.attachedVolumes];
        vols[idx] = updatedVolume;
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...group, attachedVolumes: vols };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    // AWS compute node render prop. Every group boots the head image (unified).
    const renderAwsGroupFields = useCallback((group, index, { updateGroup }) => {
        const isStorageManaged = Boolean(group.storageReference);
        const groupZone = effectiveZone(group, form);
        const groupUnoffered = Boolean(groupZone && group.instance_id && catalog.isOffered(groupZone, group.instance_id) === false);
        const gCompat = compatImage && group.instance_id ? imageShapeCompatibility(compatImage, catalog.shapeById(group.instance_id), licence) : { compatible: true };
        const gLaunch = launchCheck.byRole[`compute group ${index + 1}`];
        const gHelper = groupUnoffered ? `Not offered in ${catalog.zoneLabel(groupZone)}`
            : !gCompat.compatible ? gCompat.shapeReason
                : gLaunch && gLaunch.ok === false ? `AWS: ${gLaunch.message}` : undefined;
        return (
            <>
                {isStorageManaged && (
                    <Box mb={1}><Chip label="Media Storage" size="small" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} /></Box>
                )}
                <ComputeNodeGroupFields
                    group={group} index={index} updateGroup={updateGroup}
                    provider="aws" maxCount={250}
                    onInstanceChange={isStorageManaged ? undefined : (i, id) => updateGroup(i, 'instance_id', id)}
                    allowedInstanceIds={marketplace.allowedComputeIds}
                    disableStackFields={marketplace.disableStackFields || isStorageManaged}
                    disabled={disableInputs || isStorageManaged}
                    instanceOptions={catalog.shapes} instanceLoading={catalog.catalogLoading}
                    getInstanceOptionState={(inst) => catalog.instanceOptionState(inst.instance_id, groupZone, compatImage, licence)}
                    getInstancePrice={priceFor}
                    instanceError={Boolean(gHelper)}
                    instanceHelperText={gHelper}
                >
                    <div style={inputGroupStyle}>
                        <ZoneSelector provider="aws" regionId={form?.regionId || ''}
                            value={group.zone_id || form?.zoneId || ''}
                            onChange={(zoneId) => updateGroup(index, 'zone_id', zoneId)}
                            options={catalog.zones} loading={catalog.catalogLoading}
                            getOptionState={(z) => catalog.zoneOptionState(z.zone_id, [group.instance_id])}
                            disabled={disableInputs || isStorageManaged} />
                        {isEfaCompatible(group.instance_id, instanceSizes) && (
                            <FormControlLabel
                                control={
                                    <Checkbox style={{ color: '#2196F3' }}
                                        checked={isEfaCompatible(group.instance_id, instanceSizes) && group.efa && !zonesDiffer}
                                        onChange={() => { if (!isStorageManaged && !zonesDiffer) updateGroup(index, 'efa', !group.efa); }}
                                        disabled={isStorageManaged || zonesDiffer} />
                                }
                                label={<Tooltip title={zonesDiffer ? "EFA not supported across zones" : "Elastic Fabric Adapter"}><span>EFA</span></Tooltip>}
                            />
                        )}
                    </div>
                    {availableStorageConfigs.length > 0 && !isStorageManaged && (
                        <Box mt={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Volumes</Typography>
                                <button
                                    onClick={(e) => {
                                        volumeDropdownAnchorEl.current[index] = e.currentTarget;
                                        setVolumeDropdownOpen(prev => ({ ...prev, [index]: true }));
                                    }}
                                    className={classes.grayButton}
                                    style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: '#fafafa' }}
                                >
                                    + Add Volume
                                </button>
                                <Menu
                                    anchorEl={volumeDropdownAnchorEl.current[index]}
                                    open={Boolean(volumeDropdownOpen[index])}
                                    onClose={() => setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }))}
                                    PaperProps={{ style: { maxHeight: 300, minWidth: 250 } }}
                                >
                                    {availableStorageConfigs.map(config => (
                                        <MenuItem key={config.id} onClick={() => {
                                            addVolumeToNode(index, config.id);
                                            setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }));
                                        }}>
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                                <Typography variant="body2" style={{ fontSize: '12px' }}>{config.name || 'Unknown'}</Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            <VolumeManager
                                volumes={group.attachedVolumes || []}
                                availableStorageConfigs={availableStorageConfigs}
                                onVolumeUpdate={handleVolumeUpdate}
                                onVolumeDelete={removeVolumeFromNode}
                                nodeGroupIndex={index}
                            />
                        </Box>
                    )}
                </ComputeNodeGroupFields>
            </>
        );
    }, [form, marketplace, disableInputs, zonesDiffer, instanceSizes, catalog, compatImage, launchCheck, licence, priceFor, availableStorageConfigs, addVolumeToNode, handleVolumeUpdate, removeVolumeFromNode, volumeDropdownOpen, classes.grayButton]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="aws" providerLabel="Amazon Web Services" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} formInvalidReason={formInvalidReason} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                            subtitle={clusterEstimate ? `- Est. $${clusterEstimate.total.toFixed(2)}/hr${clusterEstimate.trial ? ' (free trial may apply)' : ''}` : undefined}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">
                                <BasicInformationSection
                                    form={form} setField={setField} provider="aws"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                    availableArchs={marketplace.availableArchs || undefined}
                                    selectedArch={marketplace.selectedArch || undefined}
                                    onArchChange={marketplace.setArch}
                                    onOsChange={marketplace.hasVariants ? marketplace.setOs : undefined}
                                    hideImage
                                />

                                {needsSubscription && (
                                    <MarketplaceSubscribePrompt
                                        productName={softwareEntry?.productName || (marketplace.isMarketStack ? marketplace.title : null) || 'this Marketplace product'}
                                        credentialName={credentialName}
                                        listingUrl={subscribeListingUrl}
                                        onRecheck={recheckSubscription}
                                        checking={pricingChecking}
                                    />
                                )}

                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard icon="cloud" title="Infrastructure" fillHeight
                                            action={form.credentialId && form.regionId ? (
                                                <ZoneAvailabilityIndicator {...indicator} />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="aws" value={form.credentialId} onChange={(id) => setField('credentialId', id)} />
                                                <RegionSelector provider="aws" value={form.regionId || ''} onChange={(id) => setField('regionId', id)}
                                                    allowedRegionIds={marketplace.config?.allowed_regions?.length > 0 ? marketplace.config.allowed_regions : undefined}
                                                    options={catalog.regions} loading={catalog.regionsLoading}
                                                    disabled={!form.credentialId} disabledHint="Select a Credential first"
                                                    error={Boolean(catalog.regionsError)}
                                                    helperText={catalog.regionsError ? `Could not list regions from AWS: ${catalog.regionsError}` : undefined} />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <ZoneSelector provider="aws" regionId={form.regionId || ''} value={form.zoneId || ''} onChange={(id) => setField('zoneId', id)}
                                                    options={catalog.zones} loading={catalog.catalogLoading}
                                                    getOptionState={(z) => catalog.zoneOptionState(z.zone_id, headZoneInstanceIds(form))}
                                                    error={headZoneMissing.length > 0}
                                                    helperText={headZoneMissing.length > 0 ? `Does not offer ${headZoneMissing.join(', ')}` : undefined} />
                                                <InstanceSizeSelector provider="aws" value={form.serverInstanceId || ''}
                                                    onChange={(id) => setField('serverInstanceId', id)}
                                                    allowedInstanceIds={marketplace.config?.instance_type} label="Head Node Size"
                                                    options={catalog.shapes} loading={catalog.catalogLoading}
                                                    disabled={!form.regionId} disabledHint="Select a Region first"
                                                    getOptionState={(inst) => catalog.instanceOptionState(inst.instance_id, form.zoneId, compatImage, licence)}
                                                    getPrice={priceFor}
                                                    error={Boolean(headShapeHelper)}
                                                    helperText={headShapeHelper} />
                                                <PricingPermissionHint status={catalog.pricing?.status} message={catalog.pricing?.message} />
                                            </div>
                                            {catalog.catalogError && (
                                                <Typography variant="body2" color="error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    Could not load {form.regionId} from AWS: {catalog.catalogError}
                                                    <Button size="small" variant="outlined" onClick={catalog.retry}>Retry</Button>
                                                </Typography>
                                            )}
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        {/* Image picker sits next to Infrastructure so the user fills
                                            credential + region first, then selects the image (as on IBM). */}
                                        <ImageSelectionSection
                                            form={form} setField={setField} provider="aws"
                                            disableStackFields={marketplace.disableStackFields}
                                            stacks={awsStacks}
                                            getStackOptionState={(stack) => awsStackOptionState(stack, { form, catalog, credentialsById, licenceOf: licenceOfImage })}
                                            renderStackExtras={(stack) => {
                                                const image = catalog.imagesReady ? catalog.resolveImage(stack.data.prefix, 'heidi') : null;
                                                return (
                                                    <>
                                                        {stack.data.type && <Chip size="small" style={stackTypeChipStyle} label={stack.data.type} />}
                                                        {image && <AwsImageChips image={image} software={softwareOf(image)} />}
                                                    </>
                                                );
                                            }}
                                            cloudImages={marketplace.isMarketStack ? [] : cloudImages}
                                            cloudImagesLoading={catalog.catalogLoading}
                                            cloudImagesPlaceholder={cloudImagesPlaceholder}
                                            getCloudImageOptionState={(img) => awsImageOptionState(img.image, { form, catalog, licence: licenceFor(img.software, img.software?.productName) })}
                                            renderCloudImageExtras={(img) => <AwsImageChips image={img.image} software={img.software} />}
                                            onDeleteCloudImage={(img) => setDeleteRow({ prefix: img.image.prefix, image: img.image, stack: stackByPrefix[img.image.prefix] || null })}
                                            imageNameFor={(img) => img.image.prefix}
                                            emptyLabel={marketplace.isMarketStack ? (marketplaceAmiState?.name || 'Marketplace image') : undefined}
                                            error={imageMissing}
                                            helperText={legacyImageNote || imageHelperText || imageInfoText}
                                        />
                                    </div>
                                </div>

                                <ResourceManagerConfiguration form={form} setField={setField} lockManager={Boolean(marketplace.config?.resource_manager)} />

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="aws" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        <div style={columnStyle}>
                                            <NetworkSettingsSection form={form} setField={setField} fillHeight />
                                        </div>
                                        <div style={columnStyle}>
                                            <CapacityReservationsSection form={form} setField={setField} fillHeight showAutoExpire />
                                        </div>
                                    </div>
                                )}
                                {advanced && (
                                    <StorageSettingsSection form={form} setField={setField} provider="aws"
                                        volSizeLimits={volSizeLimits} shareOptions={[{ value: 'nfs', label: 'NFS' }]} />
                                )}
                                {advanced && (
                                    <StorageConfigurationSection
                                        value={form.storageConfigurations || []}
                                        onChange={handleStorageConfigurationChange}
                                        disabled={disableInputs} cloudProvider="aws"
                                        regionId={form.regionId} zoneId={form.zoneId}
                                        nodeGroups={form.nodeGroups || []}
                                        serverVolumes={form.serverAttachedVolumes || []}
                                        onNodeGroupsChange={(groups) => setField('nodeGroups', groups)}
                                        zones={catalog.zones} awsInstanceSizes={catalog.shapes} />
                                )}

                                <HeadNodePDSSection form={form} setField={setField} />

                                <ComputeNodesSection form={form} setField={setField} disabled={disableInputs}
                                    maxGroups={250} renderGroupFields={renderAwsGroupFields} />

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                        <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="sm" fullWidth>
                            {deleteRow && (
                                <DeleteImageDialog
                                    row={deleteRow}
                                    credentialId={form.credentialId}
                                    credentialName={credentialName}
                                    region={form.regionId}
                                    onClose={() => setDeleteRow(null)}
                                    onDeleted={() => {
                                        if (deleteRow.image && form.serverImageId === deleteRow.image.imageId) {
                                            setField({ serverImageId: '', serverImageName: '' });
                                        }
                                        catalog.retry();
                                    }}
                                />
                            )}
                        </Dialog>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default AwsCluster;
